import React, { useEffect, useState } from 'react';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  adminAutomationService, 
  AutomationWithUser 
} from '../../services/adminAutomationService';
import { platformEmailService } from '../../services/platformEmailService';
import { Timestamp } from 'firebase/firestore';
import { AutomationAction, AutomationStep } from '../../services/automationService';
import QuickEditPopover from './QuickEditPopover';
import EmailHtmlPreview from './EmailHtmlPreview';
import {
  buildActionStepEmailConfigUpdate,
  isLikelyEmailActionStep,
  resolveActionStepEmailConfig,
} from '../../utils/actionStepEmailConfig';

interface AutomationFocusedViewProps {
  automation: AutomationWithUser | null;
  onUpdate: () => void;
  showSystemEmailLibrary?: boolean;
  systemEmailAutomations?: AutomationWithUser[];
  compactSystemLayout?: boolean;
}

const AutomationFocusedView: React.FC<AutomationFocusedViewProps> = ({ 
  automation, 
  onUpdate,
  showSystemEmailLibrary = false,
  systemEmailAutomations = [],
  compactSystemLayout = false,
}) => {
  const { isDark } = useAdminTheme();
  const { showSuccess, showError } = useNotification();
  
  const [editingStep, setEditingStep] = useState<{ step: AutomationStep; index: number } | null>(null);
  const [popoverAnchor, setPopoverAnchor] = useState<DOMRect | null>(null);
  const [triggering, setTriggering] = useState(false);
  const [selectedEmailStepId, setSelectedEmailStepId] = useState<string | null>(null);
  const [htmlDraft, setHtmlDraft] = useState('');
  const [subjectDraft, setSubjectDraft] = useState('');
  const [savingHtml, setSavingHtml] = useState(false);

  const handleStepClick = (e: React.MouseEvent, step: AutomationStep, index: number) => {
    setEditingStep({ step, index });
    setPopoverAnchor(e.currentTarget.getBoundingClientRect());
  };

  const getEmailConfig = (step: AutomationStep) => resolveActionStepEmailConfig(step.config);

  const getEmailStepsForAutomation = (item: AutomationWithUser) => {
    return (item.steps || []).filter((step) => isLikelyEmailActionStep(step));
  };

  const emailSteps = (automation?.steps || []).filter((step) => isLikelyEmailActionStep(step));

  useEffect(() => {
    if (!automation) return;
    if (emailSteps.length === 0) {
      setSelectedEmailStepId(null);
      setHtmlDraft('');
      setSubjectDraft('');
      return;
    }

    const fallbackStep = emailSteps[0];
    const selected = emailSteps.find((step) => step.id === selectedEmailStepId) || fallbackStep;
    setSelectedEmailStepId(selected.id);
    const cfg = getEmailConfig(selected);
    setHtmlDraft(cfg.htmlBody);
    setSubjectDraft(cfg.subject);
  }, [automation?.id, automation?.updatedAt, emailSteps.length, selectedEmailStepId]);

  const handleSelectEmailStep = (stepId: string) => {
    const step = emailSteps.find((s) => s.id === stepId);
    if (!step) return;
    setSelectedEmailStepId(step.id);
    const cfg = getEmailConfig(step);
    setHtmlDraft(cfg.htmlBody);
    setSubjectDraft(cfg.subject);
  };

  const handleSaveEmailHtml = async () => {
    if (!automation) return;
    if (!selectedEmailStepId) return;
    const step = emailSteps.find((s) => s.id === selectedEmailStepId);
    if (!step) return;

    const cfg = (step.config ?? {}) as unknown as Record<string, unknown>;

    const updates: Partial<AutomationStep> = {
      config: buildActionStepEmailConfigUpdate(cfg, {
        subject: subjectDraft,
        htmlBody: htmlDraft,
      }) as unknown as AutomationAction,
    };

    try {
      setSavingHtml(true);
      await adminAutomationService.updateAutomationStep(automation.id!, selectedEmailStepId, updates);
      // Bust the platformEmailService cache so the next email send picks up
      // the new template immediately instead of waiting for the 5-min TTL.
      const systemKey = (automation as any).systemKey as string | undefined;
      if (systemKey) platformEmailService.invalidateCachedAutomation(systemKey);
      showSuccess('Email HTML updated');
      onUpdate();
    } catch (error) {
      console.error('Failed updating email html', error);
      showError('Failed to update email HTML');
    } finally {
      setSavingHtml(false);
    }
  };

  const handleTriggerNow = async () => {
    if (!automation?.id) return;
    const email = window.prompt("Enter recipient email address for manual trigger:");
    if (!email) return;

    try {
      setTriggering(true);
      await adminAutomationService.triggerAutomationManually(automation.id, email);
      showSuccess(`Automation triggered successfully for ${email}`);
    } catch (error) {
      console.error('Error triggering automation:', error);
      showError('Failed to trigger automation');
    } finally {
      setTriggering(false);
    }
  };

  const handleStatusChange = async (newStatus: 'active' | 'paused') => {
    if (!automation?.id) return;
    try {
      await adminAutomationService.forceUpdateAutomationStatus(automation.id, newStatus);
      const systemKey = (automation as any).systemKey as string | undefined;
      if (systemKey) platformEmailService.invalidateCachedAutomation(systemKey);
      showSuccess(`Automation ${newStatus} successfully`);
      onUpdate();
    } catch (error) {
      showError('Failed to update status');
    }
  };

  const getStepIcon = (type: string) => {
    switch (type) {
      case 'trigger': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      );
      case 'action': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
      case 'delay': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
      case 'condition': return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3" />
        </svg>
      );
      default: return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      );
    }
  };

  if (!automation && !showSystemEmailLibrary) {
    return (
      <div className={`flex flex-col items-center justify-center h-full p-8 text-center ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
        <div className={`w-24 h-24 mb-6 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
          <svg className={`w-12 h-12 ${isDark ? 'text-slate-600' : 'text-slate-200'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className={`text-[15px] font-medium mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          Select an automation to view details
        </h3>
        <p className={`max-w-[300px] text-[13px] mx-auto ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
          Choose from your list on the left to see the workflow steps, analytics, and manage settings.
        </p>
      </div>
    );
  }

  if (!automation && showSystemEmailLibrary) {
    return (
      <div className={`flex flex-col h-full ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
        <div className={`flex items-center justify-between px-8 py-4 border-b ${isDark ? 'border-slate-800' : 'border-slate-100'} sticky top-0 ${isDark ? 'bg-[#0f172a]/95' : 'bg-white/95'} backdrop-blur-md z-20`}>
          <div className="flex flex-col">
            <h2 className={`text-[16px] font-semibold ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>System Emails</h2>
            <p className={`text-[12px] mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              All system templates in rendered HTML format.
            </p>
          </div>
        </div>

        <div className={`flex-1 p-8 overflow-y-auto ${isDark ? 'bg-[#0f172a]' : 'bg-slate-50/30'}`}>
          <div className="max-w-4xl mx-auto">
            <div className={`p-6 rounded-3xl border shadow-sm ${isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-slate-100'}`}>
              <h3 className={`text-[15px] font-semibold mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                System Email HTML Library
              </h3>
              <p className={`text-[12px] mb-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Select any message from the middle list to edit; previews are shown below.
              </p>

              <div className="space-y-5">
                {systemEmailAutomations.length === 0 ? (
                  <div className={`text-[13px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    No system automations found in this folder.
                  </div>
                ) : (
                  systemEmailAutomations.map((item) => {
                    const steps = getEmailStepsForAutomation(item);
                    return (
                      <div
                        key={item.id}
                        className={`rounded-2xl border p-4 ${isDark ? 'border-slate-700 bg-[#111827]' : 'border-slate-200 bg-slate-50/40'}`}
                      >
                        <div className={`text-[14px] font-semibold mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{item.name}</div>
                        <div className={`text-[11px] mb-3 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          status: {item.status} {item.systemKey ? `• key: ${item.systemKey}` : ''}
                        </div>

                        {steps.length === 0 ? (
                          <div className={`text-[12px] italic ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                            No email action with HTML found.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {steps.map((step) => {
                              const cfg = getEmailConfig(step);
                              return (
                                <div key={step.id} className={`rounded-xl border p-3 ${isDark ? 'border-slate-700 bg-[#0f172a]' : 'border-slate-200 bg-white'}`}>
                                  <div className={`text-[12px] font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                                    {step.title || step.id}
                                  </div>
                                  <div className={`text-[12px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Subject: {cfg.subject || '(no subject)'}
                                  </div>
                                  <EmailHtmlPreview
                                    html={cfg.htmlBody}
                                    minHeight={260}
                                    className={`w-full rounded-lg border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // TypeScript narrowing: below this line automation is guaranteed.
  if (!automation) {
    return null;
  }

  return (
    <div className={`flex flex-col h-full ${isDark ? 'bg-[#0f172a]' : 'bg-white'}`}>
      {/* Premium Toolbar */}
      <div className={`flex items-center justify-between ${compactSystemLayout ? 'px-5 py-3' : 'px-8 py-4'} border-b ${isDark ? 'border-slate-800' : 'border-slate-100'} sticky top-0 ${isDark ? 'bg-[#0f172a]/95' : 'bg-white/95'} backdrop-blur-md z-20`}>
        <div className="flex items-center gap-6">
          <button 
             onClick={() => window.history.back()}
             className={`p-2 rounded-full transition-colors ${
               isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
             }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          
          <div className="flex flex-col">
            <h2 className={`text-[16px] font-semibold ${isDark ? 'text-slate-50' : 'text-slate-900'}`}>{automation.name}</h2>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                automation.status === 'active' 
                  ? (isDark ? 'text-emerald-400 border-emerald-400/20 bg-emerald-400/10' : 'text-emerald-700 border-emerald-100 bg-emerald-50') 
                  : (isDark ? 'text-amber-400 border-amber-400/20 bg-amber-400/10' : 'text-amber-700 border-amber-100 bg-amber-50')
              }`}>
                {automation.status}
              </span>
              <span className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                ID: {automation.id}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button 
            onClick={handleTriggerNow}
            disabled={triggering}
            className={`flex items-center gap-2 px-5 py-2 rounded-full text-[13px] font-bold transition-all shadow-sm active:scale-95 ${
              isDark 
                ? 'bg-[#1e293b] text-slate-100 border border-slate-700 hover:bg-slate-800' 
                : 'bg-white text-[#0B57D0] border border-slate-200 hover:bg-slate-50'
            }`}
          >
            <svg className={`w-4 h-4 ${triggering ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d={triggering ? 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15' : 'M12 19l9 2-9-18-9 18 9-2zm0 0v-8'} />
            </svg>
            {triggering ? 'Triggering...' : 'Trigger now'}
          </button>
          
          <div className={`w-px h-6 mx-2 ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}></div>

          <div className="flex items-center gap-1">
            <button 
              onClick={() => handleStatusChange(automation.status === 'active' ? 'paused' : 'active')}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              title={automation.status === 'active' ? 'Pause' : 'Activate'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={automation.status === 'active' ? 'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z' : 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z'} />
              </svg>
            </button>
            <button 
              onClick={() => window.open(`/automations/edit/${automation.id}`, '_blank')}
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'text-slate-400 hover:bg-slate-800 hover:text-slate-100' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
              title="Open in Builder"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </button>
            <button 
              className={`p-2 rounded-lg transition-colors ${
                isDark ? 'text-red-400 hover:bg-red-900/20' : 'text-red-600 hover:bg-red-50'
              }`}
              title="Delete"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-4v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Main Flow Content */}
      <div className={`flex-1 ${compactSystemLayout ? 'p-4' : 'p-8'} overflow-y-auto ${isDark ? 'bg-[#0f172a]' : 'bg-slate-50/30'}`}>
        <div className={`${compactSystemLayout ? 'max-w-5xl space-y-4 pb-6' : 'max-w-3xl space-y-10 pb-20'} mx-auto`}>
          
          {/* Metadata Card */}
          <div className={`${compactSystemLayout ? 'p-5' : 'p-8'} rounded-3xl border shadow-sm ${isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-slate-100'}`}>
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center text-[18px] font-bold ${isDark ? 'bg-slate-800 text-slate-300' : 'bg-[#E8F0FE] text-[#0B57D0]'}`}>
                   {automation.userName?.charAt(0) || 'S'}
                </div>
                <div>
                  <div className={`text-[15px] font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                    {automation.userName || 'Scubamail System'}
                  </div>
                  <div className={`text-[12px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                    Created by {automation.userEmail || 'admin@scubamail.io'}
                  </div>
                </div>
              </div>
              <div className={`text-[11px] font-medium ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                {automation.updatedAt instanceof Timestamp 
                  ? automation.updatedAt.toDate().toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) 
                  : new Date(automation.updatedAt as any).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            </div>
            <p className={`text-[14px] leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              {automation.description || "This automation is configured to run based on the sequence defined below."}
            </p>
          </div>

          {showSystemEmailLibrary && (
            <div className={`p-6 rounded-3xl border shadow-sm ${isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-slate-100'}`}>
              <h3 className={`text-[15px] font-semibold mb-1 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                System Email HTML Library
              </h3>
              <p className={`text-[12px] mb-5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                All system email templates are shown below with rendered HTML.
              </p>

              <div className="space-y-5">
                {systemEmailAutomations.length === 0 ? (
                  <div className={`text-[13px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    No system automations found.
                  </div>
                ) : (
                  systemEmailAutomations.map((item) => {
                    const steps = getEmailStepsForAutomation(item);
                    return (
                      <div
                        key={item.id}
                        className={`rounded-2xl border p-4 ${isDark ? 'border-slate-700 bg-[#111827]' : 'border-slate-200 bg-slate-50/40'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <div className={`text-[14px] font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{item.name}</div>
                            <div className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              status: {item.status} {item.systemKey ? `• key: ${item.systemKey}` : ''}
                            </div>
                          </div>
                        </div>

                        {steps.length === 0 ? (
                          <div className={`text-[12px] italic ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                            No email action with HTML found.
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {steps.map((step) => {
                              const cfg = getEmailConfig(step);
                              return (
                                <div key={step.id} className={`rounded-xl border p-3 ${isDark ? 'border-slate-700 bg-[#0f172a]' : 'border-slate-200 bg-white'}`}>
                                  <div className={`text-[12px] font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
                                    {step.title || step.id}
                                  </div>
                                  <div className={`text-[12px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    Subject: {cfg.subject || '(no subject)'}
                                  </div>
                                  <EmailHtmlPreview
                                    html={cfg.htmlBody}
                                    minHeight={260}
                                    className={`w-full rounded-lg border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          <div className={`p-6 rounded-3xl border shadow-sm ${isDark ? 'bg-[#1e293b] border-slate-700' : 'bg-white border-slate-100'}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className={`text-[15px] font-semibold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>Email HTML</h3>
                <p className={`text-[12px] mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Preview and edit the exact HTML body used by this automation step.
                </p>
              </div>
              {emailSteps.length > 0 && (
                <button
                  onClick={handleSaveEmailHtml}
                  disabled={savingHtml}
                  className={`px-4 py-2 rounded-lg text-[12px] font-semibold ${
                    isDark ? 'bg-blue-600 text-white hover:bg-blue-500 disabled:bg-slate-700' : 'bg-[#0B57D0] text-white hover:bg-[#0842A0] disabled:bg-slate-300'
                  }`}
                >
                  {savingHtml ? 'Saving...' : 'Save HTML'}
                </button>
              )}
            </div>

            {emailSteps.length === 0 ? (
              <div className={`text-[13px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                No email action step found in this automation.
              </div>
            ) : (
              <>
                <div className="mb-4">
                  <label className={`block text-[11px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Email step</label>
                  <select
                    value={selectedEmailStepId || ''}
                    onChange={(e) => handleSelectEmailStep(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-[13px] ${
                      isDark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  >
                    {emailSteps.map((step) => (
                      <option key={step.id} value={step.id}>
                        {step.title || step.id}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-4">
                  <label className={`block text-[11px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Subject</label>
                  <input
                    value={subjectDraft}
                    onChange={(e) => setSubjectDraft(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border text-[13px] ${
                      isDark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
                    }`}
                  />
                </div>

                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-[11px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>HTML code</label>
                    <textarea
                      value={htmlDraft}
                      onChange={(e) => setHtmlDraft(e.target.value)}
                      rows={16}
                      spellCheck={false}
                      className={`w-full px-3 py-2 rounded-lg border text-[12px] font-mono ${
                        isDark ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
                      }`}
                    />
                  </div>
                  <div>
                    <label className={`block text-[11px] mb-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Rendered preview</label>
                    <EmailHtmlPreview
                      html={htmlDraft}
                      minHeight={360}
                      className={`w-full rounded-lg border ${
                        isDark ? 'border-slate-700' : 'border-slate-200'
                      }`}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {!compactSystemLayout && (
            <div className="flex flex-col items-center">
              {automation.steps && automation.steps.map((step, index) => (
              <React.Fragment key={step.id}>
                <button
                  onClick={(e) => handleStepClick(e, step, index)}
                  className={`group relative w-full flex items-center p-5 rounded-2xl border-2 transition-all duration-300 ${
                    isDark 
                      ? 'bg-[#111827] border-slate-800 hover:border-blue-600/50 hover:bg-[#1e293b]' 
                      : 'bg-white border-white shadow-sm hover:border-blue-600/30 hover:shadow-lg'
                  }`}
                >
                  {/* Step ID Index */}
                  <div className={`absolute -left-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full border-2 flex items-center justify-center text-[11px] font-bold transition-all ${
                    isDark 
                      ? 'bg-slate-900 border-slate-800 text-slate-500 group-hover:bg-blue-600/20 group-hover:text-blue-400' 
                      : 'bg-white border-slate-100 text-slate-300 group-hover:border-[#0B57D0]/20 group-hover:text-[#0B57D0]'
                  }`}>
                    {index + 1}
                  </div>

                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center transition-all ${
                    isDark ? 'bg-slate-800 text-blue-400 group-hover:bg-blue-600/10' : 'bg-slate-50 text-slate-400 group-hover:bg-[#E8F0FE] group-hover:text-[#0B57D0]'
                  }`}>
                    {getStepIcon(step.type)}
                  </div>

                  <div className="flex-1 text-left ml-4">
                    <div className={`text-[10px] font-bold uppercase tracking-[0.1em] mb-1 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                      {step.type}
                    </div>
                    <div className={`text-[14px] font-bold ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
                      {step.title}
                    </div>
                    <div className={`text-[12px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-500'} italic`}>
                      {step.description}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className={`text-[11px] font-bold px-3 py-1 rounded-full transition-all ${
                      isDark ? 'bg-slate-800 text-slate-500 group-hover:text-blue-400' : 'bg-slate-50 text-slate-400 group-hover:bg-[#E8F0FE] group-hover:text-[#0B57D0]'
                    }`}>
                      Quick Edit
                    </div>
                    <svg className={`w-5 h-5 transition-transform group-hover:translate-x-1 ${isDark ? 'text-slate-700' : 'text-slate-200'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>

                {/* Vertical Connector */}
                {index < (automation.steps?.length || 0) - 1 && (
                  <div className="h-10 w-px bg-slate-200 dark:bg-slate-800 my-1 relative">
                    <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-slate-200 dark:bg-slate-800"></div>
                  </div>
                )}
              </React.Fragment>
              ))}
            </div>
          )}
        </div>
      </div>

      {!compactSystemLayout && editingStep && popoverAnchor && (
        <QuickEditPopover 
          step={editingStep.step}
          automationId={automation.id!}
          anchorRect={popoverAnchor}
          onClose={() => setEditingStep(null)}
          onSuccess={() => {
            setEditingStep(null);
            onUpdate();
          }}
        />
      )}
    </div>
  );
};

export default AutomationFocusedView;
